import { useState } from 'react'
import { cdApi } from '../../api/changeDetection'
import { CD_DIFF_STATUS_LABEL, CD_REPORT_STATUS_LABEL } from '../../types/changeDetection'
import type {
  CdDiff,
  CdDiffCategory,
  CdDiffItem,
  CdDiffVisible,
  CdObservation,
  CdReport,
  CdReportFormat,
} from '../../types/changeDetection'

// ── 결과 표시용 포맷터·범례 (voxel-based-change-detection RightPanel.jsx 참조) ──
function fmtVol(m3: number | null | undefined): string {
  if (m3 == null || Number.isNaN(m3)) return '—'
  const a = Math.abs(m3)
  if (a >= 1_000_000) return `${(m3 / 1_000_000).toFixed(3)} km³`
  if (a >= 1_000) return `${(m3 / 1_000).toFixed(2)}k m³`
  return `${m3.toFixed(1)} m³`
}

const RESULT_LAYERS = [
  { key: 'add', name: '추가된 복셀', desc: 'B에만 존재 (신규)', tag: 'ADD', dot: 'bg-emerald-500', tagCls: 'bg-emerald-50 text-emerald-700' },
  { key: 'rem', name: '제거된 복셀', desc: 'A에만 존재 (소실)', tag: 'REM', dot: 'bg-red-500', tagCls: 'bg-red-50 text-red-700' },
  { key: 'chg', name: '유지된 복셀', desc: 'A·B 모두 존재·형상 변화', tag: 'CHG', dot: 'bg-amber-500', tagCls: 'bg-amber-50 text-amber-700' },
] as const

// 시점쌍 1건의 변화탐지 결과 — 날짜 헤더 + 색상 범례 + 통계 박스
function DiffItemResult({ item }: { item: CdDiffItem }) {
  const added = item.addedVolume ?? 0
  const removed = item.removedVolume ?? 0
  const changed = item.changedVolume ?? 0
  const net = added - removed

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2.5">
      <div className="text-[11px] font-medium text-slate-500">
        {item.sourceObservedAt} <span className="text-slate-300">→</span> {item.targetObservedAt}
      </div>

      <div className="mt-2 space-y-1">
        {RESULT_LAYERS.map((l) => (
          <div key={l.key} className="flex items-center gap-2">
            <span className={`h-2 w-2 shrink-0 rounded-sm ${l.dot}`} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-medium text-slate-700">{l.name}</div>
              <div className="text-[10px] text-slate-400">{l.desc}</div>
            </div>
            <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${l.tagCls}`}>{l.tag}</span>
          </div>
        ))}
      </div>

      <div className="mt-2 space-y-1 border-t border-slate-200 pt-2">
        <div className="flex justify-between text-[11px]">
          <span className="text-slate-500">추가 체적</span>
          <span className="font-semibold text-emerald-600">+{fmtVol(added)}</span>
        </div>
        <div className="flex justify-between text-[11px]">
          <span className="text-slate-500">제거 체적</span>
          <span className="font-semibold text-red-600">-{fmtVol(removed)}</span>
        </div>
        {changed > 0 && (
          <div className="flex justify-between text-[11px]">
            <span className="text-slate-500">유지 체적</span>
            <span className="font-semibold text-amber-600">{fmtVol(changed)}</span>
          </div>
        )}
        <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 text-[11px]">
          <span className="font-medium text-slate-600">순 변화</span>
          <span className={`font-bold ${net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {net >= 0 ? '+' : ''}
            {fmtVol(net)}
          </span>
        </div>
      </div>
    </div>
  )
}

// 시계열 diff — 시점쌍별 순 변화(추가 − 제거) 인라인 SVG 막대 차트
function TimeSeriesBars({ items }: { items: CdDiffItem[] }) {
  const rows = items
    .filter((it) => it.status === 'SUCCEEDED')
    .map((it) => ({
      label: String(it.targetObservedAt).slice(2),
      net: (it.addedVolume ?? 0) - (it.removedVolume ?? 0),
    }))
  if (rows.length < 2) return null
  const max = Math.max(...rows.map((r) => Math.abs(r.net)), 1)
  const W = 260
  const H = 96
  const pad = 16
  const zero = H / 2
  const slot = (W - pad * 2) / rows.length
  const bw = Math.min(22, slot * 0.6)
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2">
      <div className="text-[10px] font-medium text-slate-500 mb-1">시점쌍별 순 변화</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="시계열 순 변화 막대 차트">
        <line x1={pad} y1={zero} x2={W - pad} y2={zero} stroke="#cbd5e1" strokeWidth="1" />
        {rows.map((r, i) => {
          const cx = pad + slot * (i + 0.5)
          const h = (Math.abs(r.net) / max) * (zero - 8)
          const up = r.net >= 0
          return (
            <g key={i}>
              <rect
                x={cx - bw / 2}
                y={up ? zero - h : zero}
                width={bw}
                height={Math.max(h, 1)}
                fill={up ? '#22c55e' : '#ef4444'}
                rx="1.5"
              />
              <text x={cx} y={H - 3} textAnchor="middle" className="fill-slate-400" style={{ fontSize: 7 }}>
                {r.label}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

interface Props {
  projectId: number
  observations: CdObservation[]
  selectedIds: number[]
  diffs: CdDiff[]
  shownDiffId: number | null
  diffVisible: CdDiffVisible
  onChanged: () => void
  onToggleShow: (diffId: number) => void
  onToggleDiffVisible: (cat: CdDiffCategory) => void
  reportsByDiff: Record<number, CdReport[]>
  reportBusy: Record<string, boolean>
  onLoadReports: (diffId: number) => void
  onCreateReport: (diffId: number, format: CdReportFormat) => void
  onDownloadReport: (diffId: number, report: CdReport) => void
  onDeleteReport: (diffId: number, reportId: number) => void
  compare: { diffId: number; mode: 'blink' | 'split' } | null
  splitPos: number
  onSetCompare: (diffId: number, mode: 'blink' | 'split' | null) => void
  onSetSplitPos: (v: number) => void
}

const ACTIVE = new Set(['QUEUED', 'RUNNING'])

export default function CdDiffPanel({
  projectId,
  observations,
  selectedIds,
  diffs,
  shownDiffId,
  diffVisible,
  onChanged,
  onToggleShow,
  onToggleDiffVisible,
  reportsByDiff,
  reportBusy,
  onLoadReports,
  onCreateReport,
  onDownloadReport,
  onDeleteReport,
  compare,
  splitPos,
  onSetCompare,
  onSetSplitPos,
}: Props) {
  const [mode, setMode] = useState<'ab' | 'timeseries'>('ab')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // 결과 상세는 기본 접힘 — 펼친 diff id 집합
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())

  const toggleExpand = (id: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
        onLoadReports(id)
      }
      return next
    })

  const readyCount = observations.filter((o) => o.voxelStatus === 'SUCCEEDED').length
  const canSubmit = mode === 'ab' ? selectedIds.length === 2 : readyCount >= 2

  const handleSubmit = async () => {
    setError('')
    if (!name.trim()) {
      setError('변화탐지 이름을 입력해주세요.')
      return
    }
    setSubmitting(true)
    try {
      if (mode === 'ab') {
        await cdApi.createAbDiff(projectId, {
          name: name.trim(),
          sourceObservationId: selectedIds[0],
          targetObservationId: selectedIds[1],
        })
      } else {
        await cdApi.createTimeSeriesDiff(projectId, { name: name.trim() })
      }
      setName('')
      onChanged()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? '변화탐지 요청에 실패했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('이 변화탐지 결과를 삭제할까요?')) return
    try {
      await cdApi.deleteDiff(id)
      onChanged()
    } catch {
      alert('삭제에 실패했습니다.')
    }
  }

  const handleCancel = async (id: number) => {
    try {
      await cdApi.cancelDiff(id)
      onChanged()
    } catch {
      alert('취소에 실패했습니다.')
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-bold text-slate-800">변화탐지 수행</h3>
        <div className="mt-2 flex gap-2">
          {(['ab', 'timeseries'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                mode === m ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {m === 'ab' ? 'A / B (2개 선택)' : '전체 시계열'}
            </button>
          ))}
        </div>

        <p className="mt-2 text-xs text-slate-500">
          {mode === 'ab'
            ? `voxel 완료된 관측 2개를 좌측에서 선택하세요. (현재 ${selectedIds.length}개 선택)`
            : `voxel 완료된 관측 ${readyCount}개로 인접 시점쌍을 모두 비교합니다.`}
        </p>

        <div className="mt-2 flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="변화탐지 이름"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white"
          >
            {submitting ? '요청 중...' : '변화탐지 수행'}
          </button>
        </div>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-800 mb-2">변화탐지 결과</h3>
        {diffs.length === 0 ? (
          <p className="text-xs text-slate-400">아직 결과가 없습니다.</p>
        ) : (
          <ul className="space-y-2">
            {diffs.map((d) => {
              const hasResult = d.items.some((it) => it.status === 'SUCCEEDED')
              const shown = shownDiffId === d.id
              const expanded = expandedIds.has(d.id)
              return (
                <li key={d.id} className={`border rounded-lg p-3 ${shown ? 'border-blue-400 bg-blue-50/40' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{d.name}</p>
                      <p className="text-xs text-slate-500">
                        {d.type === 'A_B' ? 'A/B' : '시계열'} · {CD_DIFF_STATUS_LABEL[d.status]} · 시점쌍 {d.items.length}개
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {hasResult && (
                        <button
                          onClick={() => toggleExpand(d.id)}
                          aria-expanded={expanded}
                          className="text-xs font-medium text-slate-400 hover:text-slate-700"
                        >
                          {expanded ? '▾ 접기' : '▸ 상세'}
                        </button>
                      )}
                      {hasResult && (
                        <button
                          onClick={() => onToggleShow(d.id)}
                          className={`text-xs font-medium ${shown ? 'text-blue-700' : 'text-blue-600 hover:text-blue-800'}`}
                        >
                          {shown ? '지도에서 숨김' : '지도에 표시'}
                        </button>
                      )}
                      {ACTIVE.has(d.status) && (
                        <button onClick={() => handleCancel(d.id)} className="text-xs text-slate-400 hover:text-slate-700">
                          취소
                        </button>
                      )}
                      <button onClick={() => handleDelete(d.id)} className="text-xs text-slate-400 hover:text-red-600">
                        삭제
                      </button>
                    </div>
                  </div>

                  {shown && hasResult && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {RESULT_LAYERS.map((l) => {
                        const on = diffVisible[l.key]
                        return (
                          <button
                            key={l.key}
                            onClick={() => onToggleDiffVisible(l.key)}
                            title={on ? '지도에서 숨김' : '지도에 표시'}
                            className={`flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium transition ${
                              on
                                ? 'border-slate-300 bg-white text-slate-700'
                                : 'border-slate-200 bg-slate-100 text-slate-400 line-through'
                            }`}
                          >
                            <span className={`h-2 w-2 shrink-0 rounded-sm ${l.dot} ${on ? '' : 'opacity-30'}`} />
                            {l.name}
                          </button>
                        )
                      })}
                    </div>
                  )}

                  {hasResult && expanded && (
                    <div className="mt-1">
                      {d.items
                        .filter((it) => it.status === 'SUCCEEDED')
                        .map((it) => (
                          <DiffItemResult key={it.id} item={it} />
                        ))}
                    </div>
                  )}

                  {expanded && d.type === 'TIME_SERIES' && hasResult && (
                    <TimeSeriesBars items={d.items} />
                  )}

                  {expanded && d.type === 'A_B' && hasResult && (
                    <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-slate-600">비교 보기</span>
                        <div className="flex gap-1.5">
                          {(['blink', 'split'] as const).map((m) => {
                            const active = compare?.diffId === d.id && compare.mode === m
                            return (
                              <button
                                key={m}
                                onClick={() => onSetCompare(d.id, active ? null : m)}
                                className={`rounded border px-2 py-0.5 text-[10px] font-medium transition ${
                                  active
                                    ? 'border-blue-600 bg-blue-600 text-white'
                                    : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                                }`}
                              >
                                {m === 'blink' ? '블링크' : '화면 분할'}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                      {compare?.diffId === d.id && compare.mode === 'split' && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-[10px] text-slate-400">A</span>
                          <input
                            type="range"
                            min={0.05}
                            max={0.95}
                            step={0.01}
                            value={splitPos}
                            onChange={(e) => onSetSplitPos(Number(e.target.value))}
                            className="flex-1 accent-blue-600"
                          />
                          <span className="text-[10px] text-slate-400">B</span>
                        </div>
                      )}
                      <p className="mt-1 text-[10px] text-slate-400">
                        {compare?.diffId === d.id
                          ? compare.mode === 'blink'
                            ? '두 시점 원본을 교대로 점멸합니다.'
                            : '분할선을 드래그해 좌(기준)·우(대상)를 비교합니다.'
                          : '두 관측의 원본 3D 타일을 겹쳐 비교합니다.'}
                      </p>
                    </div>
                  )}

                  {hasResult && expanded && (d.status === 'SUCCEEDED' || d.status === 'PARTIAL_FAILED') && (
                    <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-slate-600">보고서</span>
                        <div className="flex gap-1.5">
                          {(['PDF', 'HWPX'] as const).map((fmt) => {
                            const busy = reportBusy[`${d.id}:${fmt}`]
                            return (
                              <button
                                key={fmt}
                                onClick={() => onCreateReport(d.id, fmt)}
                                disabled={busy}
                                className="rounded border border-blue-300 px-2 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                              >
                                {busy ? '생성 중...' : `${fmt} 생성`}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                      {(reportsByDiff[d.id] ?? []).length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {(reportsByDiff[d.id] ?? []).map((r) => (
                            <li key={r.id} className="flex items-center justify-between gap-2 text-[10px]">
                              <span className="min-w-0 truncate text-slate-500">
                                <span className="font-semibold text-slate-700">{r.format}</span>
                                {' · '}
                                {r.createdAt?.slice(0, 16).replace('T', ' ')}
                                {' · '}
                                <span className={r.status === 'FAILED' ? 'text-red-600' : 'text-slate-500'}>
                                  {CD_REPORT_STATUS_LABEL[r.status]}
                                </span>
                              </span>
                              <span className="flex shrink-0 gap-2">
                                {r.status === 'SUCCEEDED' && (
                                  <button
                                    onClick={() => onDownloadReport(d.id, r)}
                                    className="font-medium text-blue-600 hover:text-blue-800"
                                  >
                                    다운로드
                                  </button>
                                )}
                                <button
                                  onClick={() => onDeleteReport(d.id, r.id)}
                                  className="text-slate-400 hover:text-red-600"
                                >
                                  삭제
                                </button>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {d.errorMessage && <p className="mt-1 text-xs text-red-600">{d.errorMessage}</p>}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
